/**
 * ConfirmDialog — shared primitive for v3 confirmation dialogs.
 *
 * Composes the existing v3 primitives (`Dialog + DialogShell + Button +
 * Input`) into the icon-badge / title / description / summary-card /
 * footer shape that's been duplicated across four feature confirms
 * (CSV export, generate claims report, bulk acknowledge, delete user).
 *
 * What it owns:
 *   - The full visual frame (header, body, footer slots).
 *   - Optional structured summary card (`summary` prop, array of
 *     {label, value}). Pass undefined to skip.
 *   - Optional typed-confirmation guard (`confirmWord` prop). When set,
 *     a text input is rendered after summary/children; Confirm stays
 *     disabled until the user types the exact word (case-insensitive,
 *     trimmed). Local state, auto-resets when the dialog closes.
 *   - The close-while-loading lockout: `onClose` is a no-op while
 *     `isLoading` is true (matches the existing four call sites).
 *   - Danger vs primary variant: drives badge background/colour and
 *     the Confirm button variant.
 *   - Acknowledge-only use: omit `confirmLabel` and the Confirm button
 *     is not rendered at all, leaving the ghost button (labelled OK by
 *     the caller) as the only way out.
 *
 * What it does NOT own:
 *   - Localisation. Callers pass `confirmLabel` / `cancelLabel`
 *     already-translated. Same for `title` / `description`.
 *   - The trigger. Callers render their own button / menu item /
 *     toolbar action that flips `open`.
 *   - The Confirm side effect. `onConfirm` is the caller's mutation;
 *     `isLoading` reflects its in-flight state.
 *
 * Custom body: pass `children` to slot extra content between the
 * SummaryCard and the typed-confirmation field. Used by Generate
 * Claims Report (a "download xlsx" checkbox) and CSV export (a
 * bespoke big-number summary card that doesn't fit the standard
 * `summary` shape).
 */
import { useEffect, useState } from 'react';

import { styled } from '../styled';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Input } from '../Input';
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogFooterActions,
  DialogHeader,
  DialogIconBadge,
  DialogTitle,
  DialogTitleBlock,
} from '../DialogShell';

const SummaryCard = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 18px',
  borderRadius: 12,
  background: 'var(--qhs-bg)',
  gap: 10,
});

const SummaryRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  fontSize: 14,
});

const SummaryLabel = styled('span')({ color: 'var(--qhs-text-3)' });
const SummaryValue = styled('span')({ color: 'var(--qhs-text-1)', fontWeight: 600 });

const ConfirmField = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 16,
});

const ConfirmLabel = styled('span')({
  fontSize: 13,
  color: 'var(--qhs-text-2)',
  lineHeight: 1.4,
});

const dangerBadgeStyle = {
  background: 'var(--qhs-st-critical-bg)',
  color: 'var(--qhs-error)',
};

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  variant = 'primary',
  icon,
  title,
  description,
  summary,
  confirmWord,
  confirmWordPrompt,
  confirmLabel,
  cancelLabel,
  isLoading = false,
  isConfirmDisabled = false,
  children,
}) => {
  const [confirmText, setConfirmText] = useState('');

  // Reset the typed-confirmation field whenever the dialog closes so
  // the next open starts from an empty input. Callers do not have to
  // wire this themselves.
  useEffect(() => {
    if (!open) setConfirmText('');
  }, [open]);

  const guardedClose = () => {
    if (!isLoading) onClose?.();
  };

  const confirmWordMatches =
    !confirmWord ||
    confirmText.trim().toUpperCase() === confirmWord.trim().toUpperCase();

  const confirmButtonVariant = variant === 'danger' ? 'danger' : 'primary';
  const badgeStyle = variant === 'danger' ? dangerBadgeStyle : undefined;

  return (
    <Dialog
      open={open}
      onClose={guardedClose}
      ariaLabel={typeof title === 'string' ? title : undefined}
      header={
        <DialogHeader>
          <DialogIconBadge style={badgeStyle}>{icon}</DialogIconBadge>
          <DialogTitleBlock>
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogTitleBlock>
        </DialogHeader>
      }
      footer={
        <DialogFooter>
          <DialogFooterActions>
            <Button variant="ghost" onClick={guardedClose} disabled={isLoading}>
              {cancelLabel}
            </Button>
            {/* An acknowledge-only dialog passes no `confirmLabel`: the
                ghost button is the whole footer, and there is no second
                action to name. Rendering it anyway produced a nameless
                primary button next to it. */}
            {confirmLabel && (
              <Button
                variant={confirmButtonVariant}
                onClick={onConfirm}
                disabled={isLoading || isConfirmDisabled || !confirmWordMatches}
              >
                {confirmLabel}
              </Button>
            )}
          </DialogFooterActions>
        </DialogFooter>
      }
    >
      {/* Body slot only renders when there is something to show.
          A "title + description → cancel/confirm" dialog (Remove
          access, Discard changes, etc.) has no summary, no children,
          and no typed-confirm guard — passing an empty DialogBody to
          Dialog would leave a 44-px void framed by header and footer
          hairlines that reads as two horizontal lines in the middle
          of the modal. */}
      {(summary?.length || children || confirmWord) ? (
        <DialogBody>
          {summary?.length ? (
            <SummaryCard>
              {summary.map(row => (
                <SummaryRow key={row.label}>
                  <SummaryLabel>{row.label}</SummaryLabel>
                  <SummaryValue>{row.value}</SummaryValue>
                </SummaryRow>
              ))}
            </SummaryCard>
          ) : null}
          {children}
          {confirmWord ? (
            <ConfirmField>
              <ConfirmLabel>
                {confirmWordPrompt || `Type ${confirmWord} to confirm`}
              </ConfirmLabel>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={confirmWord}
                disabled={isLoading}
                autoComplete="off"
                fullWidth
              />
            </ConfirmField>
          ) : null}
        </DialogBody>
      ) : null}
    </Dialog>
  );
};

export default ConfirmDialog;
